import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';
import { raiseEvent as raiseEventByTarget } from 'api/events/endpoints/raiseEvent';

export interface EventAction {
  type: string;
  parameters?: Array<{ name: string; value: string }>;
  node_definition_id: number | null;
  queue_id: number | null;
}

export interface EventConfig {
  enableFileEvent: boolean;
  worker: string;
  connection_id?: number | null;
  directory: string;
  namingpattern: string;
  move: boolean;
  movedirectory?: string;
  poll_interval?: number | null;
  min_file_age: number;
  ssh_connection_status_updates?: Array<{
    status: string;
    detail?: string | null;
    worker_name?: string | null;
    timestamp: string;
  }>;
}

export interface Event {
  event_id: number;
  zone_name: string;
  name: string;
  description?: string;
  type: string;
  // nap-backend forces both to null for processEvent rows (routes/events.js);
  // the edit modal reads these defensively and normalizes them on mount.
  action: EventAction | null;
  config: EventConfig | null;
  // Folder support (nap-backend #1076). Zone-scoped. `folder_id` is always
  // sent (null clears); `folder_path` is response-only display data.
  folder_id: number | null;
  folder_path?: FolderPath | null;
}

interface EventState {
  items: Event[];
  status: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  error: string | null;
  config: { header_key: string; header_type: string; label: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    fetchEvents: boolean;
  };
}

const initialState: EventState = {
  items: [],
  status: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 2000,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchEvents: false,
  },
};

export const fetchEvents = createAsyncThunk('events/fetchEvents', async (params: Record<string, any>) => {
  const response = await api.get('/events', { params });
  return response.data;
});

export const fetchEventById = createAsyncThunk('events/fetchEventById', async (event_id: number) => {
  const response = await api.get(`/events/${event_id}`);
  return response.data;
});

export const createEvent = createAsyncThunk('events/createEvent', async (event: Omit<Event, 'event_id'>, { rejectWithValue }) => {
  try {
    const response = await api.post('/events', event);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || { error: 'Failed to create event.' });
  }
});

export const updateEvent = createAsyncThunk('events/updateEvent', async (event: Event, { rejectWithValue }) => {
  try {
    const response = await api.put(`/events/${event.event_id}`, event);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || { error: 'Failed to update event.' });
  }
});

export const deleteEvent = createAsyncThunk('events/deleteEvent', async (args: { zone_name: string; name: string }) => {
  await api.delete(`/events/${encodeURIComponent(args.zone_name)}/${encodeURIComponent(args.name)}`);
  return args;
});

// The raise/discard thunks below delegate to the `api/events/endpoints`
// wrappers rather than calling `api.post` themselves — one implementation of
// each command, shared with the Live Events cards (issue #999). The thunks
// stay because the events list page dispatches them and reads their
// pending/rejected state; they contribute the redux plumbing, not the request.

export const raiseFileEvent = createAsyncThunk('events/raiseFileEvent', async (args: { event_id: number }, { rejectWithValue, getState }) => {
  try {
    const { event_id } = args;
    const state = getState() as { events: EventState };
    const event = state.events.items.find((e) => e.event_id === event_id);

    if (!event) {
      return rejectWithValue({
        error: 'Event not found in state.',
      });
    }

    await raiseEventByTarget({ zoneName: event.zone_name, eventName: event.name, eventType: 'fileEvent' });
    return { event_id };
  } catch (error: any) {
    return rejectWithValue(error.response?.data || { error: 'Failed to raise file event.' });
  }
});

export const raiseProcessEvent = createAsyncThunk('events/raiseProcessEvent', async (args: { event_id: number }, { rejectWithValue, getState }) => {
  try {
    const { event_id } = args;
    const state = getState() as { events: EventState };
    const event = state.events.items.find((e) => e.event_id === event_id);

    if (!event) {
      return rejectWithValue({
        error: 'Event not found in state.',
      });
    }

    await raiseEventByTarget({ zoneName: event.zone_name, eventName: event.name, eventType: 'processEvent' });
    return { event_id };
  } catch (error: any) {
    return rejectWithValue(error.response?.data || { error: 'Failed to raise process event.' });
  }
});

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchEvents = true;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const { events, pagination, config, filtersConfig } = payload;

        if (Array.isArray(events)) state.items = events;

        if (config) state.config = config;
        if (filtersConfig) state.filtersConfig = filtersConfig;

        if (pagination && typeof pagination.total_records === 'number') {
          state.currentPage = pagination.current_page ?? 1;
          state.totalPages = pagination.total_pages ?? 1;
          state.totalItems = pagination.total_records ?? 0;
          state.pageSize = pagination.limit ?? state.pageSize;
        } else {
          const { currentPage, totalPages, totalItems, pageSize } = payload;
          if (currentPage != null) state.currentPage = currentPage;
          if (totalPages != null) state.totalPages = totalPages;
          if (totalItems != null) state.totalItems = totalItems;
          if (pageSize != null) state.pageSize = pageSize;
        }

        state.status = 'succeeded';
        state.loading.fetchEvents = false;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Something went wrong';
        state.loading.fetchEvents = false;
      })
      .addCase(fetchEventById.fulfilled, (state, action) => {
        const idx = state.items.findIndex((event) => event.event_id === action.payload.event_id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.status = 'failed';
        if (typeof action.payload === 'string') {
          state.error = action.payload;
        } else if (action.payload && typeof action.payload === 'object') {
          state.error = (action.payload as any).error || (action.payload as any).message || 'Failed to create event';
        } else {
          state.error = 'Failed to create event';
        }
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        const index = state.items.findIndex((event) => event.event_id === action.payload.event_id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.status = 'failed';
        if (typeof action.payload === 'string') {
          state.error = action.payload;
        } else if (action.payload && typeof action.payload === 'object') {
          state.error = (action.payload as any).error || (action.payload as any).message || 'Failed to update event';
        } else {
          state.error = 'Failed to update event';
        }
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const { zone_name, name } = action.payload as {
          zone_name: string;
          name: string;
        };
        state.items = state.items.filter((event) => !(event.zone_name === zone_name && event.name === name));
      })
      .addCase(raiseFileEvent.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(raiseFileEvent.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(raiseFileEvent.rejected, (state, action) => {
        state.status = 'failed';
        if (typeof action.payload === 'string') {
          state.error = action.payload;
        } else if (action.payload && typeof action.payload === 'object') {
          state.error = (action.payload as any).error || (action.payload as any).message || 'Failed to raise file event';
        } else {
          state.error = 'Failed to raise file event';
        }
      })
      .addCase(raiseProcessEvent.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(raiseProcessEvent.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(raiseProcessEvent.rejected, (state, action) => {
        state.status = 'failed';
        if (typeof action.payload === 'string') {
          state.error = action.payload;
        } else if (action.payload && typeof action.payload === 'object') {
          state.error = (action.payload as any).error || (action.payload as any).message || 'Failed to raise process event';
        } else {
          state.error = 'Failed to raise process event';
        }
      });
  },
});

export default eventsSlice.reducer;
